import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '@/services/letsparkyClient';
import { User as ApiUser, LoginResponse, ApiError, handleApiError } from '@/types/api';
// Types for UserLoginParams and ApiUser will be inferred as 'any' for now
// import { UserLoginParams, User as ApiUser } from '@letsparky/api-v2-client';

// Define proper types for API responses
interface AuthResponse {
  token: string;
  user: ApiUser;
}

// Attempt to use types from the client namespace if they are not top-level exports
// This is a guess; actual structure might differ. 
// You may need to check the actual .d.ts file of the package.
type UserLoginParams = any;

export type UserRole = 'SYSTEM' | 'TENANT_ADMIN';

interface User {
  id: string;
  createdAt?: string;
  email: string;
  name?: string;
  role: UserRole;
  status: string;
  phoneNumber?: string
  tenantId?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  loadUserFromToken: () => Promise<void>;
}

// Helper function to map API user to app user model
const mapApiUserToAppUser = (apiUser: ApiUser): User => ({
  id: apiUser.id,
  status: apiUser.status || "",
  createdAt: apiUser.created_at || '',
  email: apiUser.email || '',
  phoneNumber: apiUser.phone_number,
  name: apiUser.name || '',
  role: apiUser.role as UserRole,
  tenantId: apiUser.tenant_id,
});

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.auth.login({ email, password });

          if (response.data?.token && response.data?.user) {
            const appUser = mapApiUserToAppUser(response.data.user);
            
            set({
              token: response.data.token,
              user: appUser,
              isAuthenticated: true,
              isLoading: false,
            });
            
            apiClient.client.setToken(response.data.token);
          } else {
            throw new Error('Login failed: No token or user data returned');
          }
        } catch (error) {
          set({
            error: handleApiError(error),
            isLoading: false,
          });
        }
      },

      logout: async () => {
        try {
          await apiClient.auth.logout();
        } catch (error) {
          console.error('Failed to logout from API', handleApiError(error));
        } finally {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          apiClient.client.setToken('');
        }
      },

      clearError: () => {
        set({ error: null });
      },

      loadUserFromToken: async () => {
        const token = get().token;
        if (!token) return;
        
        set({ isLoading: true });
        apiClient.client.setToken(token);
        
        try {
          const response = await apiClient.auth.getProfile();
          
          if (response.data) {
            const appUser = mapApiUserToAppUser(response.data);
            
            set({
              user: appUser,
              isAuthenticated: true,
            });
          } else {
            // Token might be invalid, clear it
            get().logout();
          }
        } catch (error) {
          console.error('Failed to load user from token', handleApiError(error));
          get().logout(); // Clear auth state on error
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'letsparky-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);

// Initialize auth state when the module is loaded
if (typeof window !== 'undefined') {
  useAuthStore.getState().loadUserFromToken();
} 